import { useState, useEffect } from "react";
import { Filter, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { FilterState } from "../../types/dashboard";
import { AssessmentTab } from "./TabNavigation";
import { FilterField } from "./FilterField";
import { useFilterOptions } from "../../hooks/useFilterOptions";
import type { ApiService } from "../../services/apiService";
import {
  MDS_COMPLETED_FILTER_VALUES,
  MDS_FILTER_STATUS_OPTIONS,
} from "../../utils/helper";

interface FilterConfig {
  key: string;
  label: string;
  type: "multiselect" | "singleselect" | "input" | "daysOpenSlider";
  options?: { value: string; label: string }[];
  value: string | string[];
  width?: "sm" | "md" | "lg";
  inputType?: "text" | "number";
  placeholder?: string;
  min?: string;
}

interface FilterDropdownProps {
  activeTab: AssessmentTab;
  filters: FilterState;
  onFilterChange: (field: keyof FilterState, value: string | string[]) => void;
  onClearAllFilters: () => void;
  /** Apply all filters at once (prevents race conditions with debouncing) */
  onApplyAllFilters?: (filters: FilterState) => void;
  resultCount?: number;
  showRecentlyComplete?: boolean;
  onShowRecentlyCompleteChange?: (checked: boolean) => void;
  /** Custom API service for authentication (used when passed from Dashboard) */
  api?: ApiService;
  /** Count of active Residents tab filters (for badge) */
  residentsFilterCount?: number;
  /** Toggle Residents filter card visibility */
  onResidentsFilterToggle?: () => void;
}

export function FilterDropdown({
  activeTab,
  filters,
  onFilterChange,
  // onClearAllFilters is part of the props contract but the panel uses its own
  // in-dropdown "Clear All" (handleClearAllInDropdown), so it is not consumed here.
  onApplyAllFilters,
  onShowRecentlyCompleteChange,
  api,
  residentsFilterCount,
  onResidentsFilterToggle,
}: FilterDropdownProps) {
  const [tempFilters, setTempFilters] = useState<FilterState>(filters);
  const [isMainOpen, setIsMainOpen] = useState(false);

  // Fetch dynamic filter options from backend (uses passed api if provided).
  // NETSOL-12075: scope the Assessment Type list to the active tab's category so
  // the MDS tab shows official MDS types and the Care Assessments tab shows UDA
  // types (the Residents tab is not category-scoped).
  const filterCategory =
    activeTab === "mds" ? "MDS" : activeTab === "care" ? "UDA" : undefined;
  const filterOptions = useFilterOptions(api, filterCategory);

  // Derive checkbox state: on MDS tab we work in raw EHR codes
  // (NETSOL-11379), so check for any of the completed/pending-submission
  // codes; on the Care tab we still work in bucket enums. Struck/Deleted is
  // intentionally excluded — it's a separate, explicitly-selected filter,
  // not part of "recently complete".
  const isCompletedSelected =
    activeTab === "mds"
      ? MDS_COMPLETED_FILTER_VALUES.some((v) =>
          tempFilters.quickStatusFilters.includes(v),
        )
      : tempFilters.quickStatusFilters.includes("COMPLETED");

  // Sync temp filters when main filters change (e.g., when switching tabs)
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  // Count active filters
  const activeFilterCount = Object.entries(filters).reduce(
    (count, [key, value]) => {
      if (
        (key === "quickStatusFilters" ||
          key === "unsignedSections" ||
          key === "station" ||
          key === "payer" ||
          key === "assessmentType") &&
        Array.isArray(value) &&
        value.length > 0
      )
        return count + 1;
      // dueDate is owned by the standalone DateRangeFilter control, not this
      // panel, so it must not contribute to the panel's filter count badge.
      if (
        key !== "dueDate" &&
        typeof value === "string" &&
        value !== "" &&
        value !== "all"
      )
        return count + 1;
      return count;
    },
    0,
  );

  // Use appropriate filter count based on active tab
  const displayFilterCount =
    activeTab === "residents" ? residentsFilterCount : activeFilterCount;

  // MDS Section options (A through Z)
  const getMdsSectionOptions = () => [
    { value: "A", label: "Section A" },
    { value: "B", label: "Section B" },
    { value: "C", label: "Section C" },
    { value: "D", label: "Section D" },
    { value: "E", label: "Section E" },
    { value: "F", label: "Section F" },
    { value: "G", label: "Section G" },
    { value: "GG", label: "Section GG" },
    { value: "H", label: "Section H" },
    { value: "I", label: "Section I" },
    { value: "J", label: "Section J" },
    { value: "K", label: "Section K" },
    { value: "L", label: "Section L" },
    { value: "M", label: "Section M" },
    { value: "N", label: "Section N" },
    { value: "O", label: "Section O" },
    { value: "P", label: "Section P" },
    { value: "Q", label: "Section Q" },
    { value: "V", label: "Section V" },
    { value: "X", label: "Section X" },
  ];

  // Handle temp filter changes
  const handleTempFilterChange = (
    field: keyof FilterState,
    value: string | string[],
  ) => {
    // Special handling for quickStatusFilters to manage "all" (Open Items) selection
    if (field === "quickStatusFilters" && Array.isArray(value)) {
      // Get the previously displayed value (empty state shows as ["all"])
      const prevDisplayedValue = tempFilters.quickStatusFilters.length === 0
        ? ["all"]
        : tempFilters.quickStatusFilters;

      // Check if user clicked "all" (Open Items) - it's newly added to the selection
      const allWasAdded = value.includes("all") && !prevDisplayedValue.includes("all");

      if (allWasAdded) {
        // User clicked "Open Items" - reset to default (empty = backend excludes
        // COMPLETED/CANCELLED/STRUCK/PENDING_SUBMISSION)
        setTempFilters((prev) => ({ ...prev, [field]: [] }));
        return;
      }

      // User clicked a specific status - remove "all" and keep specific statuses
      const filteredValue = value.filter(v => v !== "all");
      setTempFilters((prev) => ({ ...prev, [field]: filteredValue }));
      return;
    }
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Handle apply filters
  const handleApply = () => {
    // Apply all filters at once to prevent race conditions with debouncing
    if (onApplyAllFilters) {
      onApplyAllFilters(tempFilters);
    } else {
      // Fallback to individual updates (may have race conditions with debounce)
      Object.entries(tempFilters).forEach(([field, value]) => {
        onFilterChange(field as keyof FilterState, value);
      });
    }
    setIsMainOpen(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setTempFilters(filters);
    setIsMainOpen(false);
  };

  // Handle clear all in dropdown - only clears temp state; user must click Apply
  const handleClearAllInDropdown = () => {
    const clearedFilters: FilterState = {
      status: "",
      residentName: "",
      location: "",
      admissionDate: "",
      mdsOcType: "",
      referenceWindow: "",
      ardDate: "",
      // dueDate is owned by the standalone DateRangeFilter control; preserve it
      // so clearing the panel filters does not reset the date range.
      dueDate: tempFilters.dueDate,
      wdsr02: "",
      assignedServices: "",
      nextAction: "",
      caaCompletion: "",
      requiredTransmissionDate: "",
      globalSearch: "",
      quickStatusFilters: [],
      payer: [],
      assessmentType: [],
      station: [],
      building: "all",
      mdsSection: "all",
      assignedOwner: "all",
      daysOpen: "",
      unsignedSections: [],
    };
    setTempFilters(clearedFilters);
    // Keep dialog open so user can review and click Apply or Cancel
  };

  // Handle checkbox toggle — on MDS tab, add/remove the raw-code values
  // that correspond to the COMPLETED/PENDING_SUBMISSION buckets
  // (NETSOL-11379); on the Care tab, keep the bucket enum as before. STRUCK
  // is deliberately left out of both — struck/deleted items are only shown
  // when explicitly selected via the Status filter, not bundled into
  // "recently complete".
  const handleShowRecentlyCompleteChange = (checked: boolean) => {
    const addValues =
      activeTab === "mds"
        ? MDS_COMPLETED_FILTER_VALUES
        : ["COMPLETED"];

    setTempFilters((prev) => {
      const currentStatuses = [...prev.quickStatusFilters];

      if (checked) {
        for (const v of addValues) {
          if (!currentStatuses.includes(v)) {
            currentStatuses.push(v);
          }
        }
      } else {
        for (const v of addValues) {
          const idx = currentStatuses.indexOf(v);
          if (idx > -1) {
            currentStatuses.splice(idx, 1);
          }
        }
      }

      return { ...prev, quickStatusFilters: currentStatuses };
    });

    // Also call parent handler if provided (for external state sync)
    if (onShowRecentlyCompleteChange) {
      onShowRecentlyCompleteChange(checked);
    }
  };

  // Get payer options from dynamic filter options
  const getPayerOptions = () => filterOptions.payers;

  // Get assessment type options from dynamic filter options
  const getAssessmentTypeOptions = () => filterOptions.assessmentTypes;

  // Get station options from dynamic filter options
  const getStationOptions = () => filterOptions.stations;

  // Get building options from dynamic filter options
  const getBuildingOptions = () => filterOptions.buildings;

  // Get assigned owner options from dynamic filter options
  const getAssignedOwnerOptions = () => filterOptions.assignedOwners;

  // Filter configuration array - different filters per tab
  const getFilterConfig = (): FilterConfig[] => {
    const filters: FilterConfig[] = [];

    // MDS Tab: Status, Payer, Assessment Type, Station, Time Range, Assigned Owner
    // Care Tab: Status, Payer, Assessment Type, Station, Time Range, Days Open, Assigned Owner

    // Status filter - both MDS and Care tabs
    // "Open Items" is the default option (backend excludes
    // COMPLETED/CANCELLED/STRUCK/PENDING_SUBMISSION by default)
    // When quickStatusFilters is empty, show "all" (Open Items) as selected
    if (activeTab === "mds" || activeTab === "care") {
      const hasSpecificStatuses = tempFilters.quickStatusFilters.length > 0 &&
        !tempFilters.quickStatusFilters.includes("all");
      const statusLabel = hasSpecificStatuses
        ? `Status (${tempFilters.quickStatusFilters.length})`
        : "Status";
      // Show "all" as selected when no specific statuses are selected
      const statusValue = tempFilters.quickStatusFilters.length === 0
        ? ["all"]
        : tempFilters.quickStatusFilters;
      // MDS tab: show the 14 EHR-native status options (raw codes +
      // QUEUED). Care tab: keep the 6-bucket enum list from the backend.
      // See NETSOL-11379 / MDS_FILTER_STATUS_OPTIONS.
      const statusOptions =
        activeTab === "mds"
          ? MDS_FILTER_STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))
          : filterOptions.statuses;
      filters.push({
        key: "quickStatusFilters",
        label: statusLabel,
        type: "multiselect" as const,
        options: statusOptions,
        value: statusValue,
      });
    }

    // Common filters for both tabs
    filters.push(
      {
        key: "payer",
        label:
          tempFilters.payer.length > 0
            ? `Payor (${tempFilters.payer.length})`
            : "Payor",
        type: "multiselect" as const,
        options: getPayerOptions(),
        value: tempFilters.payer,
      },
      {
        key: "assessmentType",
        label:
          tempFilters.assessmentType.length > 0
            ? `Assessment Type (${tempFilters.assessmentType.length})`
            : "Assessment Type",
        type: "multiselect" as const,
        options: getAssessmentTypeOptions(),
        value: tempFilters.assessmentType,
      },
      {
        key: "station",
        label: tempFilters.station.length > 0 ? `Station (${tempFilters.station.length})` : "Station",
        type: "multiselect" as const,
        options: getStationOptions(),
        value: tempFilters.station,
      },
      {
        key: "building",
        label: "Building",
        type: "singleselect" as const,
        options: getBuildingOptions(),
        value: tempFilters.building,
      },
      {
        key: "assignedOwner",
        label: "Assigned Owner",
        type: "singleselect" as const,
        options: getAssignedOwnerOptions(),
        value: tempFilters.assignedOwner,
      }
    );

    // Unsigned Sections filter - MDS tab only (frontend filter)
    if (activeTab === "mds") {
      const hasUnsignedSections = tempFilters.unsignedSections.length > 0;
      const unsignedLabel = hasUnsignedSections
        ? `Unsigned Sections (${tempFilters.unsignedSections.length})`
        : "Unsigned Sections";
      filters.push({
        key: "unsignedSections",
        label: unsignedLabel,
        type: "multiselect" as const,
        options: getMdsSectionOptions(),
        value: tempFilters.unsignedSections,
      });
    }

    // Days Open filter - Care tab only
    if (activeTab === "care") {
      filters.push({
        key: "daysOpen",
        label: "Days Open",
        type: "daysOpenSlider" as const,
        value: tempFilters.daysOpen,
      });
    }

    return filters;
  };

  return (
    <div>
      <Button
        variant="outline"
        onClick={() => {
          if (activeTab === "residents" && onResidentsFilterToggle) {
            onResidentsFilterToggle();
          } else {
            setIsMainOpen(!isMainOpen);
          }
        }}
        disabled={activeTab !== "residents" && filterOptions.isLoading}
        className="h-8 px-4 py-2 bg-white border border-solid border-[#228189] text-[#228189] hover:bg-gray-50 flex items-center gap-1 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {activeTab !== "residents" && filterOptions.isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#228189]" />
        ) : (
          <Filter className="w-4 h-4 fill-[#228189]" />
        )}
        <span className="text-sm font-bold">FILTER</span>
        {(displayFilterCount ?? 0) > 0 && (
          <Badge
            variant="default"
            className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-[#228189] text-white"
          >
            {displayFilterCount}
          </Badge>
        )}
      </Button>

      {isMainOpen && activeTab !== "residents" && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsMainOpen(false)}
          />

          {/* Main Filter Panel */}
          <div
            className="absolute top-full left-0 right-0 mx-4 mt-2 z-50 bg-white shadow-[0px_3px_14px_2px_rgba(0,0,0,0.12)] rounded-md border border-solid border-border p-6"
            style={{
              transform: "none",
              position: "absolute",
            }}
          >
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#1B3647]">
                  Filters
                </h3>
                <Button
                  className="text-[#228189] uppercase p-0 hover:text-[#1a6b72]"
                  variant="ghost"
                  onClick={handleClearAllInDropdown}
                >
                  Clear All
                </Button>
              </div>

              {/* Show Recently Complete Checkbox */}


              {/* Loading indicator or Filter Fields */}
              {filterOptions.isLoading ? (
                <div className="flex items-center justify-center py-8 text-[#228189]">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  <span className="text-sm">Loading filter options...</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {getFilterConfig().map((filterConfig) => (
                    <FilterField
                      key={filterConfig.key}
                      label={filterConfig.label}
                      type={filterConfig.type}
                      options={filterConfig.options}
                      value={filterConfig.value}
                      onChange={(value) => handleTempFilterChange(filterConfig.key as keyof FilterState, value)}
                      width={filterConfig.width}
                      inputType={filterConfig.inputType}
                      placeholder={filterConfig.placeholder}
                      min={filterConfig.min}
                    />
                  ))}

                </div>
              )}

              {/* Footer */}
              <div className="flex justify-between items-center pt-4">
                {/* Show Recently Complete Checkbox - Left side */}
                {onShowRecentlyCompleteChange ? (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="show-recently-complete"
                      checked={isCompletedSelected}
                      onCheckedChange={(checked) =>
                        handleShowRecentlyCompleteChange(checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="show-recently-complete"
                      className="cursor-pointer"
                    >
                      Show Recently Complete
                    </Label>
                  </div>
                ) : (
                  <div />
                )}

                {/* Buttons - Right side */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApply}
                    className="px-6 bg-[#228189] hover:bg-[#1a6b72]"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
